import {DragEndEvent} from "@dnd-kit/core";
import cloneDeep from "lodash/cloneDeep";

import {ComponentOrder} from "@/lib/queries/statuspages.ts";
import {Component} from "@/lib/types/statuspages.ts";

interface HandleDropParams {
  components: Component[];
  statusPageId: number;
  updateOrder: (variables: {
    variables: {
      statusPageID: number;
      data: ComponentOrder[];
    };
  }) => Promise<unknown>;
  setComponents: (components: Component[]) => void;
}

interface SortableData {
  index: number;
}

export const createHandleDragEnd = ({components, statusPageId, updateOrder, setComponents}: HandleDropParams) => {
  return async (event: DragEndEvent) => {
    const sourceData = event.active.data.current;
    const targetData = event.over?.data.current;
    const source = sourceData?.original as Component | undefined;
    const target = targetData?.original as Component | undefined;

    if (!source || !target) {
      return;
    }
    if (source.id === target.id) {
      return;
    }
    let newData = cloneDeep(components);

    const sourceSortableIndex = (sourceData?.sortable as SortableData | undefined)?.index;
    const targetSortableIndex = (targetData?.sortable as SortableData | undefined)?.index;
    const getIsTargetExpanded = targetData?.getIsExpanded as (() => boolean) | undefined;
    const targetIsExpanded = typeof getIsTargetExpanded === "function" ? getIsTargetExpanded() : false;
    const targetHasChildren = (target.subcomponents?.length ?? 0) > 0;
    const isDroppedBelowTarget =
      sourceSortableIndex !== undefined &&
      targetSortableIndex !== undefined &&
      sourceSortableIndex < targetSortableIndex;
    const canEnterGroup = target.is_group && targetIsExpanded && targetHasChildren && isDroppedBelowTarget;

    const isTopLevel = !source.group && !target.group && (!target.is_group || source.is_group || !canEnterGroup);
    if (isTopLevel) {
      const srcPos = components.findIndex((c) => c.id === source.id);
      const trgPos = components.findIndex((c) => c.id === target.id);
      const [movedRow] = newData.splice(srcPos, 1);
      newData.splice(trgPos, 0, movedRow);
    }

    const betweenGroupsOnToGroupHeader =
      !!source.group && target.is_group && source.group !== target.id && canEnterGroup;
    if (betweenGroupsOnToGroupHeader) {
      const fromGroupIdx = newData.findIndex((c) => c.id === source.group);
      const sourceComponent = newData[fromGroupIdx]?.subcomponents.find((c) => c.id === source.id);
      if (sourceComponent) {
        const updatedSource = {...sourceComponent, group: target.id};
        newData[fromGroupIdx].subcomponents = newData[fromGroupIdx].subcomponents.filter((c) => c.id !== source.id);
        const toGroupIdx = newData.findIndex((c) => c.id === target.id);
        newData[toGroupIdx].subcomponents = [...newData[toGroupIdx].subcomponents, updatedSource];
      }
    }

    const outOfGroup = !!source.group && !target.group && !betweenGroupsOnToGroupHeader;
    if (outOfGroup) {
      const trgPos = components.findIndex((c) => c.id === target.id);
      const sourceIdx = newData.findIndex((c) => c.id === source.group);
      const sourceComponent = newData[sourceIdx].subcomponents.find((c) => c.id === source.id);
      if (sourceComponent) {
        const updatedSource = {
          ...sourceComponent,
          group: undefined,
          subcomponents: sourceComponent.subcomponents ?? [],
        };
        const firstPart = newData.splice(0, trgPos);
        newData = firstPart.concat([updatedSource]).concat(newData);
      }
    }

    const betweenGroupsOnToComponent = !!source.group && !!target.group && source.group !== target.group;
    if (betweenGroupsOnToComponent) {
      const targetIdx = newData.findIndex((c) => c.id === target.group);
      const sourceIdx = newData.findIndex((c) => c.id === source.group);
      const sourceComponent = newData[sourceIdx].subcomponents.find((c) => c.id === source.id);
      if (sourceComponent) {
        const updatedSource = {...sourceComponent, group: target.group};
        newData[targetIdx].subcomponents = [...newData[targetIdx].subcomponents, updatedSource];
        newData[sourceIdx].subcomponents = newData[sourceIdx].subcomponents.filter((c) => c.id !== source.id);
      }
    }

    const inToGroup = !source.group && !source.is_group && target.is_group && canEnterGroup;
    if (inToGroup) {
      const sourceComponent = newData.find((c) => c.id === source.id);
      if (sourceComponent) {
        const updatedSource = {...sourceComponent, group: target.id};
        newData = newData.filter((i) => i.id !== source.id);
        const parentIdx = newData.findIndex((c) => c.id === target.id);
        newData[parentIdx].subcomponents = [...newData[parentIdx].subcomponents, updatedSource];
      }
    }

    const inToGroupViaSubcomponent = !source.group && !source.is_group && !target.is_group && !!target.group;
    if (inToGroupViaSubcomponent) {
      const sourceComponent = newData.find((c) => c.id === source.id);
      if (sourceComponent) {
        const updatedSource = {...sourceComponent, group: target.group};
        newData = newData.filter((i) => i.id !== source.id);
        const parentIdx = newData.findIndex((c) => c.id === target.group);
        if (parentIdx !== -1) {
          const subs = [...newData[parentIdx].subcomponents];
          const targetSubIdx = subs.findIndex((c) => c.id === target.id);
          const insertAt = isDroppedBelowTarget ? targetSubIdx + 1 : targetSubIdx;
          subs.splice(insertAt, 0, updatedSource);
          newData[parentIdx].subcomponents = subs;
        }
      }
    }

    // A group dragged over a subcomponent of another group should be treated
    // as a top-level reorder relative to that subcomponent's parent group —
    // groups cannot be nested inside other groups.
    const groupOverSubcomponent = source.is_group && !source.group && !target.is_group && !!target.group;
    if (groupOverSubcomponent) {
      const srcPos = newData.findIndex((c) => c.id === source.id);
      if (srcPos !== -1) {
        const [movedRow] = newData.splice(srcPos, 1);
        const parentGroupIdx = newData.findIndex((c) => c.id === target.group);
        if (parentGroupIdx !== -1) {
          const insertAt = isDroppedBelowTarget ? parentGroupIdx + 1 : parentGroupIdx;
          newData.splice(insertAt, 0, movedRow);
        } else {
          newData.splice(srcPos, 0, movedRow);
        }
      }
    }

    const insideGroupReorder = !!source.group && !!target.group && source.group === target.group;
    if (insideGroupReorder) {
      const groupIdx = newData.findIndex((c) => c.id === target.group);
      const subs = [...newData[groupIdx].subcomponents];
      const sourceIdx = subs.findIndex((c) => c.id === source.id);
      const targetIdx = subs.findIndex((c) => c.id === target.id);
      const [movedRow] = subs.splice(sourceIdx, 1);
      subs.splice(targetIdx, 0, movedRow);
      newData[groupIdx].subcomponents = subs;
    }

    const payload = newData.reduce((acc: ComponentOrder[], item) => {
      acc.push({
        id: item.id,
        sortingWeight: acc.length,
        groupId: item.group,
      });
      if (outOfGroup && item.subcomponents) {
        item.subcomponents = item.subcomponents.filter((c) => c.id !== source.id);
      }
      (item.subcomponents || []).forEach((s) => {
        acc.push({
          id: s.id,
          sortingWeight: acc.length,
          groupId: s.group,
        });
      });

      return acc;
    }, []);

    setComponents(newData);

    await updateOrder({
      variables: {
        statusPageID: statusPageId,
        data: payload,
      },
    });
  };
};
